import * as React from "react";
import {
  Body1,
  Field,
  Image,
  Input,
  Button,
  tokens,
  Subtitle1,
  makeStyles,
  mergeClasses,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";

import { useAuth } from "./AuthContext";
import { AuthError } from "./authApi";

import { usePageContainerStyles } from "@shared/ui/pageContainer";

const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "420px",
    margin: "0 auto",
    gap: tokens.spacingVerticalM,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXS,
  },
  logo: {
    height: "28px",
    width: "auto",
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
  },
});

const LoginForm: React.FC = () => {
  const styles = useStyles();
  const pageContainerStyles = usePageContainerStyles();
  const { login, sessionExpiredReason } = useAuth();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  const [emailTouched, setEmailTouched] = React.useState(false);
  const [passwordTouched, setPasswordTouched] = React.useState(false);

  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const isEmailValid = email !== "" && EMAIL_PATTERN.test(email);
  const emailError = !emailTouched ? undefined : email === "" ? "Email is required" : !isEmailValid ? "Enter a valid email" : undefined;
  const passwordError = !passwordTouched ? undefined : password === "" ? "Password is required" : undefined;

  const isFormValid = isEmailValid && password !== "";

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!isFormValid) {
      setEmailTouched(true);
      setPasswordTouched(true);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await login({ email, password });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : "Couldn't sign in. Check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const notice = error ?? sessionExpiredReason;

  return (
    <form className={mergeClasses(pageContainerStyles.root, styles.root)} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <Image className={styles.logo} src="assets/nomoros-logo.svg" alt="Nomoros" />
        <Subtitle1>Sign in</Subtitle1>
        <Body1 className={styles.subtitle}>to upload attachments to your matters</Body1>
      </div>

      {notice && (
        <MessageBar intent={error ? "error" : "warning"}>
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      )}

      <Field label="Email" validationState={emailError ? "error" : "none"} validationMessage={emailError}>
        <Input
          type="email"
          value={email}
          onChange={(_, data) => setEmail(data.value)}
          onBlur={() => setEmailTouched(true)}
          required
          autoComplete="username"
        />
      </Field>

      <Field label="Password" validationState={passwordError ? "error" : "none"} validationMessage={passwordError}>
        <Input
          type="password"
          value={password}
          onChange={(_, data) => setPassword(data.value)}
          onBlur={() => setPasswordTouched(true)}
          required
          autoComplete="current-password"
        />
      </Field>

      <Button appearance="primary" type="submit" disabled={isSubmitting || !isFormValid}>
        {isSubmitting ? "Signing in..." : "Sign in"}
      </Button>
    </form>
  );
};

export default LoginForm;
